import { useState, useEffect } from 'react';

import * as styles from 'styles/index.scss';

/**
 * Bootstrap 4 Breakpoints (matching Reactstrap v8)
 * xs: 0px and up
 * sm: 576px and up
 * md: 768px and up
 * lg: 992px and up
 * xl: 1200px and up
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};

/**
 * Hook to detect current Bootstrap breakpoint
 * Uses Bootstrap 4 breakpoints to match Reactstrap behavior
 * @returns {string} Current breakpoint: 'xs', 'sm', 'md', 'lg', or 'xl'
 */
export const useBootstrapBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(() => {
    if (typeof window === 'undefined') return 'md';
    return getBootstrapBreakpoint(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setBreakpoint(getBootstrapBreakpoint(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

/**
 * Get Bootstrap breakpoint for a given width
 * @param {number} width - Screen width in pixels
 * @returns {string} Bootstrap breakpoint
 */
const getBootstrapBreakpoint = width => {
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Hook to detect if current breakpoint matches Bootstrap's mobile breakpoints
 * Mobile = xs and sm (< 768px)
 * @returns {boolean} Whether current device is mobile
 */
export const useIsMobile = () => {
  const breakpoint = useBootstrapBreakpoint();
  return breakpoint === 'xs' || breakpoint === 'sm';
};

/**
 * Hook to detect if current breakpoint matches Bootstrap's tablet breakpoint
 * Tablet = md (768px - 991px)
 * @returns {boolean} Whether current device is tablet
 */
export const useIsTablet = () => {
  const breakpoint = useBootstrapBreakpoint();
  return breakpoint === 'md';
};

/**
 * Hook to detect if current breakpoint matches Bootstrap's desktop breakpoints
 * Desktop = lg and xl (>= 992px)
 * @returns {boolean} Whether current device is desktop
 */
export const useIsDesktop = () => {
  const breakpoint = useBootstrapBreakpoint();
  return breakpoint === 'lg' || breakpoint === 'xl';
};

/**
 * Legacy hook for backward compatibility
 * Maps Bootstrap breakpoints to simple categories
 * @returns {string} 'mobile', 'tablet', or 'desktop'
 */
export const useScreenBreakpoint = () => {
  const breakpoint = useBootstrapBreakpoint();

  if (breakpoint === 'xs' || breakpoint === 'sm') return 'mobile';
  if (breakpoint === 'md') return 'tablet';
  return 'desktop';
};

/**
 * Get Bootstrap responsive classes for hiding/showing elements
 * @param {string} breakpoint - Bootstrap breakpoint ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {string} direction - 'up' (breakpoint and up) or 'down' (breakpoint and down) or 'only' (only this breakpoint)
 * @returns {string} Bootstrap utility classes
 */
export const getBootstrapDisplayClasses = (breakpoint, direction = 'up') => {
  const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
  const currentIndex = breakpoints.indexOf(breakpoint);

  if (currentIndex === -1) return '';

  switch (direction) {
    case 'up':
      return `d-none d-${breakpoint}-block`;
    case 'down':
      const nextBreakpoint = breakpoints[currentIndex + 1];
      return nextBreakpoint ? `d-block d-${nextBreakpoint}-none` : 'd-block';
    case 'only':
      const prev = breakpoints[currentIndex - 1];
      const next = breakpoints[currentIndex + 1];
      let classes = `d-none d-${breakpoint}-block`;
      if (next) classes += ` d-${next}-none`;
      return classes;
    default:
      return '';
  }
};

/**
 * Get responsive Modal props for Reactstrap based on breakpoint
 * @param {string} breakpoint - Current Bootstrap breakpoint
 * @returns {object} Reactstrap Modal props
 */
export const getResponsiveModalProps = breakpoint => {
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';

  return {
    size: isMobile ? 'xl' : 'lg',
    backdrop: isMobile ? 'static' : true,
    keyboard: !isMobile,
    fade: !isMobile,
    centered: !isMobile,
    // Add fullscreen prop for Bootstrap 4 modals on mobile
    ...(isMobile && {
      modalClassName: 'modal-fullscreen-mobile',
      contentClassName: 'h-100'
    })
  };
};

/**
 * Get responsive Button props for Reactstrap based on breakpoint
 * @param {string} breakpoint - Current Bootstrap breakpoint
 * @param {string} defaultSize - Default button size
 * @returns {object} Reactstrap Button props
 */
export const getResponsiveButtonProps = (breakpoint, defaultSize = 'md') => {
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';

  return {
    size: isMobile ? 'lg' : defaultSize,
    block: isMobile // Full-width buttons on mobile
  };
};

/**
 * Get responsive InputGroup props for Reactstrap based on breakpoint
 * @param {string} breakpoint - Current Bootstrap breakpoint
 * @returns {object} Reactstrap InputGroup props and styles
 */
export const getResponsiveInputProps = breakpoint => {
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';

  return {
    size: isMobile ? 'lg' : 'md',
    style: {
      fontSize: isMobile ? '16px' : '14px' // Prevent iOS zoom
    }
  };
};

/**
 * Bootstrap 4 compatible body scroll prevention
 * Uses Bootstrap's modal-open class behavior
 * @param {boolean} prevent Whether to prevent body scroll
 */
export const preventBodyScroll = (prevent = true) => {
  if (typeof document === 'undefined') return;

  if (prevent) {
    // Use Bootstrap's modal-open class behavior
    document.body.classList.add('modal-open');
    document.body.style.paddingRight = getScrollbarWidth() + 'px';
  } else {
    document.body.classList.remove('modal-open');
    document.body.style.paddingRight = '';
  }
};

/**
 * Get scrollbar width (used by Bootstrap for modal padding)
 * @returns {number} Scrollbar width in pixels
 */
const getScrollbarWidth = () => {
  if (typeof document === 'undefined') return 0;

  const scrollDiv = document.createElement('div');
  scrollDiv.style.cssText = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;';
  document.body.appendChild(scrollDiv);
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return scrollbarWidth;
};

/**
 * Get Bootstrap spacing utilities for responsive design
 * @param {string} property - 'margin' or 'padding'
 * @param {string} direction - 't', 'b', 'l', 'r', 'x', 'y', or '' (all sides)
 * @param {number} mobileSize - Size for mobile (0-5)
 * @param {number} desktopSize - Size for desktop (0-5)
 * @returns {string} Bootstrap utility classes
 */
export const getBootstrapSpacing = (property, direction = '', mobileSize = 2, desktopSize = 3) => {
  const prop = property.charAt(0); // 'm' or 'p'
  const dir = direction ? `-${direction}` : '';

  return `${prop}${dir}-${mobileSize} ${prop}${dir}-md-${desktopSize}`;
};

/**
 * Get responsive floating button styles using Bootstrap utilities
 * @param {string} size - Size variant ('sm', 'md', 'lg')
 * @param {string} position - Position variant ('bottom-right', 'bottom-left', 'top-right', 'top-left')
 * @param {string} breakpoint - Current breakpoint
 * @returns {object} Styles and classes for floating button
 */
export const getResponsiveFloatingButton = (size = 'md', position = 'bottom-right', breakpoint = 'md') => {
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';

  // Size mapping using Bootstrap-friendly values
  const sizeMap = {
    sm: isMobile ? '2.5rem' : '3rem',
    md: isMobile ? '3.5rem' : '4rem',
    lg: isMobile ? '4rem' : '4.5rem'
  };

  // Position mapping with Bootstrap spacing
  const positionMap = {
    'bottom-right': {
      bottom: isMobile ? `${parseInt(styles.sidebarMinHeight.replace('px$', '')) + 15}px` : '1.5rem',
      right: isMobile ? '1rem' : '1.5rem'
    },
    'bottom-left': {
      bottom: isMobile ? `${parseInt(styles.sidebarMinHeight.replace('px$', '')) + 15}px` : '1.5rem',
      left: isMobile ? '1rem' : '1.5rem'
    },
    'top-right': { top: isMobile ? '1rem' : '1.5rem', right: isMobile ? '1rem' : '1.5rem' },
    'top-left': { top: isMobile ? '1rem' : '1.5rem', left: isMobile ? '1rem' : '1.5rem' }
  };

  return {
    style: {
      width: sizeMap[size],
      height: sizeMap[size],
      ...positionMap[position]
    },
    className: `btn btn-primary rounded-circle position-fixed shadow ${isMobile ? 'btn-lg' : ''}`
  };
};
